import { fromJS, Map, OrderedMap, Set } from 'immutable';
import moment from 'moment';
import { dispatch } from 'nike-re-framejs';

import {
    setBoxesLoaders as dbSetBoxesLoaders,
    mergeLoadedBoxes as dbMergeLoadedBoxes,
    replaceLoadedBoxes as dbReplaceLoadedBoxes
} from './domain/DB';
import { initialize, initializeToday } from './domain/menu';
import {
    loadBettingDays,
    loadBoxByBetIds as n1LoadBoxByBetIds,
    loadBoxes,
    loadCmsData,
    loadMatchesOnTicket,
    loadMenu,
    loadOpenBoxes
} from './services';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { prematchSession } from './filterDialog';
import { DEFAULT_INTERVAL } from './components/dialog/byDate';
import { NIKEONE } from './context/BoxOptionsContext';
import { deepMergeNested } from 'nike-ui/src/common/immutable/merge';
import forumClient from '@/_http/axiosForumClient';
import { catcher } from '@/_http/utils';
import { navigate } from '@/routing';

export function loadMissingBoxesThenMerge(db, boxIds, boxStore, forceFetch = false, ns = []) {
    const boxIdsMap = Map(boxIds.map(boxId => [boxId, true]));
    db = db.updateIn(ns.concat(['boxStore', boxStore, 'filter', 'boxIds']), Map(), tournamentMap =>
        tournamentMap.merge(boxIdsMap)
    );
    const missingBoxIds = forceFetch
        ? boxIds
        : boxIds.filter(boxId => !db.getIn(ns.concat(['boxStore', boxStore, 'boxes', boxId])));
    if (missingBoxIds.length < 1) {
        return db;
    }
    loadBoxes(db, boxStore, boxIds, ns)
        .then(data => dispatch(mergeLoadedBoxes(data, boxStore, missingBoxIds)))
        .catch(() => []);
    return dbSetBoxesLoaders(db, missingBoxIds, true, ns);
}

const initializeFilter = db =>
    db
        .setIn(
            ['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate'],
            prematchSession.byDate.load() || DEFAULT_INTERVAL
        )
        .setIn(
            ['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'offer'],
            fromJS({
                live: !prematchSession.prematchOnly.load(),
                prematch: true,
                results: false,
                extendedMarkets: NIKEONE.shortcutMarkets
            })
        );

export const initializePrematch = guard => db => {
    loadBettingDays().then(data => dispatch(bettingDaysLoaded(data)));
    return db
        .delete('nikeone')
        .setIn(['nikeone', 'guard'], guard)
        .update(initializeFilter);
};

const bettingDaysLoaded = data => db =>
    db.setIn(
        ['nikeone', 'bettingDays'],
        OrderedMap(data.bettingDays.map(day => [day, moment(day)]).sort((e1, e2) => e1[1].valueOf() - e2[1].valueOf()))
    );
export const loadBox = (boxId, boxStore) => db => loadMissingBoxesThenMerge(db, [boxId], boxStore, true, ['nikeone']);

function parseSportId(db, slug) {
    const res = slug.split('/', 2);
    const sport = db.getIn(['nikeone', 'menu', 'sportList'], Map()).find(sport => sport.get('slug') === '/' + res[1]);
    return sport ? sport.get('boxId').split('-')[1] : null;
}

//ak je pouzity filter pre krajinu, najdi turnaje podla celeho slug-u (e.g. /futbal/slovensko) ak obsahuje nejake zaznamy zober prvy a rozparsuj box id - druha hodnota je countryId
function parseCountryId(db, slug) {
    const res = slug.split('/', 3);
    return res[2] ? db.getIn(['nikeone', 'menu', 'tournamentsBySlug', slug])?.[0]?.boxId.split('-')[2] : null;
}

export const loadOnTickets = (boxStore, slug) => db => {
    db = db
        .setIn(['nikeone', 'boxStore', boxStore, 'filter', 'onTicketSportId'], parseSportId(db, slug))
        .setIn(['nikeone', 'boxStore', boxStore, 'filter', 'onTicketCountryId'], parseCountryId(db, slug));
    loadMatchesOnTicket(db, boxStore).then(data => dispatch(mergeLoadedBoxes(data, 'filteredBoxStore')));
    return db;
};

export const refreshMenu = clear => db => {
    loadMenu(db, 'filteredBoxStore').then(data => dispatch(menuDataLoaded(data)));
    return clear ? db.deleteIn(['nikeone', 'menu']) : db;
};

const menuDataLoaded = data => db => initialize(db, data);
const todayMenuDataLoaded = data => db => initializeToday(db, data);
export const mergeLoadedBoxes = (data, boxStore, boxIds) => db => {
    const newDb = dbSetBoxesLoaders(db, boxIds, false, ['nikeone']);
    return dbMergeLoadedBoxes(newDb, boxStore, data, ['nikeone']);
};

export const cmsBetsLoad = (tournamentId, editorTips, boxStore, betId) => db => {
    loadCmsData(tournamentId, betId).then(response => {
        dispatch(mergeLoadedBoxes(response, boxStore));
    });
    return db.updateIn(['nikeone', 'boxStore', boxStore, 'editorTips'], tips =>
        deepMergeNested(tips, fromJS(editorTips))
    );
};

export const loadSuperOffer = boxStore => db =>
    db.updateIn(['nikeone'], n1db => loadMissingBoxesThenMerge(n1db, ['superoffer', 'superchance'], boxStore));

export const closeSuperOffer = boxStore => db =>
    db
        .removeIn(['nikeone', 'boxStore', boxStore, 'filter', 'boxIds', 'superoffer'])
        .removeIn(['nikeone', 'boxStore', boxStore, 'filter', 'boxIds', 'superchance']);

export const refreshBoxes = (boxStore = 'filteredBoxStore') => db => {
    Promise.all([loadOpenBoxes(db, boxStore), loadMatchesOnTicket(db, boxStore)]).then(
        ([boxesData, filteredOnTickets]) => {
            dispatch(replaceLoadedBoxes(boxesData, boxStore));
            dispatch(mergeLoadedBoxes(filteredOnTickets, boxStore));
        }
    );
    return db;
};
export const replaceLoadedBoxes = (json, boxStore) => db => dbReplaceLoadedBoxes(db, boxStore, json, ['nikeone']);

export const openTournament = (boxId, boxStore = 'filteredBoxStore') => db =>
    boxId ? db.setIn(['nikeone', 'boxStore', boxStore, 'filter', 'boxIds', boxId], true) : db;

export const toggleTournament = (boxId, boxStore = 'filteredBoxStore') => db => {
    const wasOpen = db.getIn(['nikeone', 'boxStore', boxStore, 'filter', 'boxIds', boxId], false);
    return db.setIn(['nikeone', 'boxStore', boxStore, 'filter', 'boxIds', boxId], !wasOpen);
};

export const togglePrematchOnlyFilter = db =>
    db
        .updateIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'offer'], filter =>
            filter.set('live', !filter.get('live'))
        )
        .update(db => {
            prematchSession.prematchOnly.save(
                !db.getIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'offer', 'live'])
            );
            loadMenuAndBoxes(db);
            return db;
        });

export const changePrematchByDate = (byDate, saveToSession = true) => db =>
    db
        .setIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate'], byDate)
        .removeIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'selectedMarkets'])
        .update(udb => {
            if (saveToSession) prematchSession.byDate.save(byDate);
            loadMenuAndBoxes(udb);
            return udb;
        });

export const resetFilter = (sport, country, saveToSession = true) => db => {
    // resetuju sa dve veci: 'byDate' a 'country'
    // ak sa zmenil 'byDate' musime loadnut menu, a relaodnut boxy
    // (today menu loadovat nemusime, lebo zmena 'byDate' ani 'country' sa ho netyka)
    // ak sa zmenil 'country' musime vykonat navigaciu
    const byDateChanged =
        db.getIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate']) !== DEFAULT_INTERVAL;
    const refreshPage = () => {
        if (country) {
            navigate(CURRENT_PAGE, { sportSlugPart: sport });
        }
    };
    if (byDateChanged) {
        db = db.setIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate'], DEFAULT_INTERVAL);
        if (saveToSession) prematchSession.byDate.save(null);
        loadMenuAndBoxes(db).then(refreshPage);
        return db;
    }
    refreshPage();
    return db;
};

function loadMenuAndBoxes(db) {
    return Promise.all([
        loadMenu(db, 'filteredBoxStore'),
        loadOpenBoxes(db, 'filteredBoxStore'),
        loadMatchesOnTicket(db, 'filteredBoxStore')
    ]).then(([menuData, boxesData, filteredOnTickets]) => {
        dispatch(menuDataLoaded(menuData));
        dispatch(replaceLoadedBoxes(boxesData, 'filteredBoxStore'));
        dispatch(mergeLoadedBoxes(filteredOnTickets, 'filteredBoxStore'));
    });
}

export const setToday = enabled => db =>
    db.setIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'today'], enabled);

export const loadTodayMenu = db => {
    loadMenu(
        db.setIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate'], 'TODAY'),
        'filteredBoxStore'
    ).then(data => dispatch(todayMenuDataLoaded(data)));
    return db;
};

export const changeBoxMarket = (boxStore, boxId, marketId) => db => {
    const newDb = db.setIn(['nikeone', 'boxStore', boxStore, 'filter', 'selectedMarkets', boxId], Set([marketId]));
    loadBoxes(newDb, boxStore, [boxId], ['nikeone']).then(data => {
        dispatch(mergeLoadedBoxes(data, boxStore, [boxId]));
        dispatch(marketLoaded(boxId));
    });
    return newDb.setIn(['nikeone', 'loading', 'market', boxId], true);
};

const marketLoaded = boxId => db => db.removeIn(['nikeone', 'loading', 'market', boxId]);

export const clear = guard => db => {
    if (db.getIn(['nikeone', 'guard']) !== guard) {
        return db;
    }
    // betbook je v modely nikeone, a nikeone sa zmaze napriklad pri opusteni detailu matchu - a teda by
    // bez tohto prekopirovania nefungoval preklik z matchu do analyzy, ach ...
    const bbBoxStore = db.getIn(['nikeone', 'boxStore', 'betbook']);
    return db.delete('nikeone').setIn(['nikeone', 'boxStore', 'betbook'], bbBoxStore);
};

export const initBoxStore = (boxStore, options, boxIds) => db => {
    db = db.updateIn(['nikeone', 'boxStore', boxStore], (value = Map()) => value);
    if (options) {
        const { live, prematch, results, extendedMarkets, dates } = options;
        db = db.setIn(
            ['nikeone', 'boxStore', boxStore, 'filter', 'offer'],
            fromJS({ live, prematch, results, extendedMarkets, dates })
        );
    }
    return boxIds ? loadMissingBoxesThenMerge(db, boxIds, boxStore, false, ['nikeone']) : db;
};
export const cleanBoxStore = boxStore => db => db.deleteIn(['nikeone', 'boxStore', boxStore]);

export const loadBoxByBetIds = betIds => db => {
    if (!betIds.length) {
        return db;
    }
    n1LoadBoxByBetIds(betIds).then(data => {
        dispatch(mergeLoadedBoxes(data, 'betbook', betIds));
    });
    return dbSetBoxesLoaders(db, [betIds], true);
};

const resetFilterToResultsOnly = db =>
    db
        .updateIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'offer'], offer =>
            offer
                .set('live', false)
                .set('prematch', false)
                .set('tickets', false)
                .set('results', true)
        )
        .setIn(['nikeone', 'boxStore', 'filteredBoxStore', 'filter', 'byDate'], DEFAULT_INTERVAL);

export const resultsOnly = enable => db =>
    (enable ? resetFilterToResultsOnly(db) : initializeFilter(db)).deleteIn(['nikeone', 'menu']);

export const getAnalysisCountForEvent = matchId => () => {
    forumClient
        .get('/v5/betbook/analyses/size', { params: { matchId } })
        .then(count => dispatch(setAnalysisCountForEvent(count)))
        .catch(catcher.swallow);
};

const setAnalysisCountForEvent = count => db => db.setIn(['nikeone', 'detail', 'postsCount'], count);

export const clearAnalysisCount = db => db.deleteIn(['nikeone', 'detail', 'postsCount']);

export const retrieveDiscussionCountForEvent = subjectId => () => {
    forumClient
        .get(`/v5/topics/MATCH/${subjectId}/comments/size`, { params: { subjectId } })
        .then(count => dispatch(setDiscussionCountForEvent(count <= 99 ? count : 99)))
        .catch(catcher.swallow);
};
const setDiscussionCountForEvent = count => db => db.setIn(['nikeone', 'detail', 'commentCount'], count);
